import { CustomNextPageContext } from '../../../lib/customNextPageContext';
import {
  maxPriceUrlPrefix,
  minPriceUrlPrefix,
  priceOptions,
} from '../../helpers/domain/priceOptions';
import { SrpQueryParams } from './srpRoutesHelper';
import { sanitizeObject } from '../utility/sanitizer';
import {
  getPropertyTypeFromUrlSegment,
  PropertyType,
} from '../domain/propertyTypes';

export function parseSrpParams(
  query: CustomNextPageContext['query'],
): SrpQueryParams {
  let result: SrpQueryParams = {
    q: query.q as string,
  };
  const params = [
    query.param1,
    query.param2,
    query.param3,
    query.param4,
    query.param5,
  ].filter((param) => param) as string[];

  /**
   * Try parse the price in the query params, then parse propertyType.
   * Each parser will only be used once. This is because if price exists in the url,
   * it can only be the last segment.
   */
  const parsers = [parsePropertyType, parsePrice];

  while (params.length) {
    if (parsers.length) {
      const parser = parsers.pop();
      const parsed = parser(params[params.length - 1]);
      if (parsed) {
        result = {
          ...result,
          ...parsed,
        };
        params.pop();
      }
    } else {
      /**
       * After the price and property type is parsed,
       * The remaining params are slug, parent slug and type.
       * This function will parse them.
       */
      const parsed = parseTypeAndSlug(params);
      if (parsed) {
        result = {
          ...result,
          ...parsed,
        };
      }
      break;
    }
  }

  return sanitizeObject(result);
}

function parsePrice(
  param: string,
): Partial<{ minPrice: string; maxPrice: string }> {
  const segments = param?.split('-') ?? [];
  const isPriceParam =
    segments.length === 2 &&
    priceOptions.some((option) => option.urlParam === segments[1]) &&
    (segments[0] === minPriceUrlPrefix ||
      segments[0] === maxPriceUrlPrefix ||
      priceOptions.some((option) => option.urlParam === segments[0]));

  if (!isPriceParam) {
    return null;
  }

  if (segments[0] === minPriceUrlPrefix) {
    return {
      minPrice: segments[1],
    };
  } else if (segments[0] === maxPriceUrlPrefix) {
    return {
      maxPrice: segments[1],
    };
  } else {
    return {
      minPrice: segments[0],
      maxPrice: segments[1],
    };
  }
}

function parsePropertyType(param: string): { propertyType: PropertyType } {
  const propertyType = getPropertyTypeFromUrlSegment(param);
  return propertyType ? { propertyType } : null;
}

function parseTypeAndSlug(
  restParams: string[],
): Partial<{ type: string; slug: string; parent: string }> {
  if (restParams.length === 3) {
    return {
      type: restParams[0],
      parent: restParams[1],
      slug: restParams[2],
    };
  } else if (restParams.length === 2) {
    return {
      type: restParams[0],
      slug: restParams[1],
    };
  } else if (restParams.length === 1) {
    return {
      type: restParams[0],
    };
  }

  return null;
}
